/**
 * Agent configure → Authorizations: who may authorize this agent starting work on a ticket.
 *
 * The policy belongs to the agent and saves with it. Customer approvers belong to the customer and are
 * shared by every agent; the agent's Save writes them too. Each customer's approvers load when picked.
 */
import React, { useEffect, useState } from "react";
import { Alert, Box, CircularProgress, Stack } from "@mui/material";
import LayeredAutocompleteSelect from "../../components/LayeredAutocompleteSelect";
import { FONT } from "../../styles/brandTokens";
import ApproverSummaryList from "./ApproverSummaryList";
import AuthorizationPolicyForm from "./AuthorizationPolicyForm";
import CustomerApproversForm from "./CustomerApproversForm";
import SettingsSection from "./SettingsSection";
import { AuthorizationPolicy } from "./authorizationPolicy";
import { DirectorySource } from "./customerApprovers";
import { tenantDirectorySource } from "./directorySource";
import { CustomerApproverSettings } from "./useCustomerApprovers";

export interface CustomerOption {
  label: string;
  value: string;
}

interface AgentAuthorizationsStepProps {
  policy: AuthorizationPolicy;
  onPolicyChange: (policy: AuthorizationPolicy) => void;
  approvers: CustomerApproverSettings;
  customerOptions: CustomerOption[];
  customersLoading: boolean;
  directory?: DirectorySource;
}

export default function AgentAuthorizationsStep({
  policy,
  onPolicyChange,
  approvers,
  customerOptions,
  customersLoading,
  directory = tenantDirectorySource,
}: AgentAuthorizationsStepProps) {
  const [selectedCustomerId, setSelectedCustomerId] = useState("");
  const { ensureApproversLoaded } = approvers;

  useEffect(() => {
    if (selectedCustomerId) ensureApproversLoaded(selectedCustomerId);
  }, [ensureApproversLoaded, selectedCustomerId]);

  const selectedCustomer = customerOptions.find((option) => option.value === selectedCustomerId);
  const approverStatus = selectedCustomerId ? approvers.approversStatus(selectedCustomerId) : "ready";

  return (
    <AuthorizationPolicyForm policy={policy} onChange={onPolicyChange}>
      <SettingsSection
        id="authz-customer-approvers"
        title="Customer approvers"
        description="Choose a customer, then add the admins and HR contacts from their Microsoft 365 or Google Workspace directory. Every agent uses the same approvers."
      >
        <Stack spacing={2.5}>
          <LayeredAutocompleteSelect
            label="Customer"
            value={selectedCustomerId}
            options={customerOptions}
            onChange={setSelectedCustomerId}
            loading={customersLoading}
            noOptionsText="No customers found"
            helperText="Search by customer name."
            sx={{ maxWidth: 520 }}
          />
          {!selectedCustomer ? (
            <ApproverSummaryList summary={approvers.approverSummary} onSelect={setSelectedCustomerId} />
          ) : approverStatus === "error" ? (
            <Alert severity="error" sx={{ fontFamily: FONT.sans }}>
              Couldn't load the approvers saved for {selectedCustomer.label}.
            </Alert>
          ) : approverStatus === "loading" ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
              <CircularProgress size={22} aria-label="Loading saved approvers" />
            </Box>
          ) : (
            <CustomerApproversForm
              key={selectedCustomerId}
              customerId={selectedCustomerId}
              customerName={selectedCustomer.label}
              directory={directory}
              approvers={approvers.approversFor(selectedCustomerId)}
              onChange={(next) => approvers.setApprovers(selectedCustomerId, next)}
            />
          )}
        </Stack>
      </SettingsSection>
    </AuthorizationPolicyForm>
  );
}
